import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useUserStore } from '@/stores/useUserStore';
import { useDomainOptions } from '@/api/useDomainOptions';

export type Role = 'agent' | 'supervisor' | 'customer-admin' | 'admin' | 'superadmin';
export type Feature = 'switchboard';

export type UserPermission = Role | Feature;
export const usePermissionsStore = defineStore('permissions', () => {
  const $userStore = useUserStore();
  const { data: domainOptions } = useDomainOptions();

  const hasAccess = (userPermissions: UserPermission[]) =>
    $userStore._loggedInUser.permissionGroups?.some((group) => userPermissions.includes(group));

  const _isAgent = computed(() => hasAccess(['supervisor', 'agent']));
  const _isSupervisor = computed(() => hasAccess(['supervisor']));

  const _switchboardEnabled = computed(() => hasAccess(['switchboard']));
  const _adminFeaturesEnabled = computed(() => hasAccess(['customer-admin', 'admin', 'superadmin']));
  const _ringTimeDisplayEnabled = computed(() => $userStore._loggedInUser.domain !== 'porta.wtg.cloud');

  const _hideCallHistory = computed(() => !domainOptions.value?.showCallHistory);
  const _hideCurrentBreakReason = computed(() => !domainOptions.value?.showCurrentBreakReason);
  const _showAgentMetrics = computed(() => domainOptions.value?.showAgentMetrics);
  const _allowEavesdrop = computed(() => domainOptions.value?.allowEavesdrop);
  const _anonymizeAgents = computed(() => domainOptions.value?.anonymizeAgents);

  return {
    _switchboardEnabled,
    _adminFeaturesEnabled,
    _ringTimeDisplayEnabled,
    _hideCallHistory,
    _hideCurrentBreakReason,
    _showAgentMetrics,
    _allowEavesdrop,
    _isAgent,
    _isSupervisor,
    _anonymizeAgents,
  };
});
